import React, { useRef, useEffect } from 'react';
import ReactDOMServer from 'react-dom/server';
import { Box, Grid2 } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    getBaseCanvasSettings,
    getBaseTooltipSettings,
    getBaseXAxisSettings,
    isBaseDataSeriesEmpty
} from '../../../Helper/DigitalTwin/VitalChartConfigurations';
import CartLoader from '../../../Helper/Loaders/CartLoader';
import BaseChart from './BaseChart';
import RenderChartLegend from './RenderChartLegend';
import AnnotationTooltip from './AnnotationTooltip';
import { normalizeKey } from '../../../Helper/Common/common_helpers';
import { alertTagStyles } from '../../../Helper/DigitalTwin/DataFile';
import {
    buildSeverityIntervals,
    calculateMinMaxForAxis,
    getAnnotationGroupingInterval,
    getAnnotationAxisIndex,
    getAnnotationYMax,
    getAnnotationTooltipHtml as getSharedAnnotationTooltipHtml,
    groupAnnotationsForRendering as sharedGroupAnnotationsForRendering,
    setupAnnotationTooltips as sharedSetupAnnotationTooltips,
    getSeriesForChart as sharedGetSeriesForChart,
    getIncidentIconSvg,
    svgToDataUrl,
    getTooltipHtml,
    filterAnnotationsByZoom,
    getVisibleTimePointsForZoom,
    buildTimeEventsFromGroupedPoints,
    getBoundaryTimePoints
} from '../Common/CommonFunctions';

const ContinuousSignalChart = ({
    series,
    categories,
    startDate,
    isLoading,
    resetToLatestDataDate = () => { },
    chartID = "chartID",
    metrics,
    primaryGranularity,
    visibleSeries,
    zoomResetViewRef,
    onAnyZoom,
    groupName,
    zoomedRange = null,
    setLoading = null,
    isZoomed = false,
    annotationData = [],
    batteryInfo = {},
    selectedEntity = null
}) => {

    const { t } = useTranslation();
    const tooltipCleanupRef = useRef(null);

    // Cleanup tooltips on unmount
    useEffect(() => {
        return () => {
            if (tooltipCleanupRef.current) {
                tooltipCleanupRef.current();
            }
        };
    }, []);

    // Wrapper for shared function
    const getAnnotationTooltipHtml = (timePoint) => {
        return getSharedAnnotationTooltipHtml(
            timePoint,
            AnnotationTooltip,
            ReactDOMServer.renderToStaticMarkup
        );
    };

    // Use shared functions from CommonFunctions.js
    const getFilteredAnnotations = () => {
        return filterAnnotationsByZoom(annotationData, zoomedRange);
    };

    const getVisibleTimePoints = (filteredAnnotations) => {
        const groupingFunction = (annotations) => {
            const groupingInterval = getAnnotationGroupingInterval(zoomedRange, startDate);
            return sharedGroupAnnotationsForRendering(annotations, groupingInterval);
        };
        return getVisibleTimePointsForZoom(filteredAnnotations, groupingFunction, zoomedRange, startDate);
    };

    const getChartSettings = () => {
        return {
            id: chartID,
            type: 'line',
            stacked: false,
            height: 500,
            toolbar: { show: false, },
            animations: { enabled: false, },
            zoom: {
                enabled: true,
                type: 'x',
                allowMouseWheelZoom: false,
            },
            events: {
                beforeZoom: (chartContext, { xaxis }) => {
                    if (setLoading) {
                        setLoading(true);
                    }
                    if (onAnyZoom) {
                        onAnyZoom(xaxis);
                    }
                    return {
                        xaxis: {
                            min: xaxis.min,
                            max: xaxis.max
                        }
                    };
                },
                zoomed: () => {
                    zoomResetViewRef?.current?.show();
                },
                mounted: (chartContext) => {
                    // Add custom tooltips for annotations after chart is mounted
                    setupAnnotationTooltips(chartContext);
                },
                updated: (chartContext) => {
                    // Refresh tooltips when chart updates (e.g., after zoom/unzoom)
                    setupAnnotationTooltips(chartContext);
                },
            },
            group: groupName,
        }
    }

    const getYAxisSettings = () => {

        const {y1AxisMin, y1AxisMax, y2AxisMin, y2AxisMax} = calculateMinMaxForAxis(series);

        const baseY1 = {
            showForNullSeries: true,
            position: "top",
            opposite: false,
            reversed: false,
            tickAmount: 5,
            min: y1AxisMin,
            max: y1AxisMax,
            logarithmic: false,
            labels: {
                show: true,
                showDuplicates: false,
                style: {
                    colors: '#87939D',
                    fontSize: '10px',
                    lineHeight: '16px',
                    fontFamily: 'Roboto !important',
                    fontWeight: 500,
                    cssClass: 'apexcharts-yaxis-label',
                },
                offsetX: 0,
                offsetY: 0,
                padding: 10,
                formatter: (value) => {
                    return value ? Math.round(value) : value;
                }
            },
            axisBorder: {
                show: true,
                color: '#78909C',
                offsetX: -10,
            },
            title: {
                text: "Y1",
                position: 'high',
                rotate: 0,
                offsetX: 30,
                offsetY: -235,
                style: {
                    color: undefined,
                    cssClass: 'apexcharts-yaxis-title',
                    fontSize: '12px',
                    lineHeight: '16px',
                    fontFamily: 'Roboto !important',
                    fontWeight: 400,
                },
            },
        };
        const baseY2 = {
            ...baseY1,
            opposite: true,
            min: y2AxisMin,
            max: y2AxisMax,
            title: {
                ...baseY1.title,
                text: "Y2",
                offsetX: -20,
            },
            labels: {
                ...baseY1.labels,
                offsetX: 0,
                padding: 10,
            },
            axisBorder: {
                ...baseY1.axisBorder,
                offsetX: -10,
            },
        }

        const allYAxis = [];

        let y1IsAlreadyVisible = false;
        let y2IsAlreadyVisible = false;

        metrics.forEach(metric => {
            if (metric.yaxis === 'Y1') {
                allYAxis.push({
                    ...baseY1,
                    show: !y1IsAlreadyVisible,
                    showAlways: !y1IsAlreadyVisible,
                    seriesName: metric.name,
                });
                y1IsAlreadyVisible = true;
            } else {
                allYAxis.push({
                    ...baseY2,
                    show: !y2IsAlreadyVisible,
                    showAlways: !y2IsAlreadyVisible,
                    seriesName: metric.name,
                });
                y2IsAlreadyVisible = true;
            }
        });

        if (!y1IsAlreadyVisible) {
            allYAxis.push({
                ...baseY1,
                show: true,
                seriesName: undefined,
                min: 100,
                max: 200,
                title: {
                    ...baseY1.title,
                    style: {
                        ...baseY1.title.style,
                        color: 'transparent'
                    }
                },
                labels: {
                    ...baseY1.labels,
                    style: {
                        ...baseY1.labels.style,
                        colors: ['transparent'],
                    }
                },
                axisBorder: {
                    ...baseY1.axisBorder,
                    color: 'transparent'
                },
                tooltip: {
                    enabled: false,
                },
            })
        }
        if (!y2IsAlreadyVisible) {
            allYAxis.push({
                ...baseY2,
                show: true,
                seriesName: undefined,
                min: 100,
                max: 200,
                title: {
                    ...baseY2.title,
                    style: {
                        ...baseY2.title.style,
                        color: 'transparent'
                    }
                },
                labels: {
                    ...baseY2.labels,
                    style: {
                        ...baseY2.labels.style,
                        colors: ['transparent'],
                    }
                },
                axisBorder: {
                    ...baseY2.axisBorder,
                    color: 'transparent'
                },
                tooltip: {
                    enabled: false,
                },
            })
        }

        // Add a dedicated third invisible y-axis for annotations
        // This ensures annotations always render regardless of data series state
        allYAxis.push({
            showForNullSeries: true,
            position: "top",
            show: true,
            showAlways: true,
            opposite: false,
            reversed: false,
            seriesName: 'annotations-axis',
            min: 0,
            max: 100,
            logarithmic: false,
            tickAmount: 5,
            labels: {
                show: false,
                showDuplicates: false,
            },
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
            title: {
                text: undefined,
            },
            tooltip: {
                enabled: false,
            },
        });

        return allYAxis;
      };

    const getXAxisSettings = () => {
        const settings = getBaseXAxisSettings(primaryGranularity, categories, startDate, zoomedRange)
        return settings
    }

    const getToolTipSettings = () => {
        const tooltipSettings = getBaseTooltipSettings()
        tooltipSettings.custom = (opts) => {
            // Don't show tooltip for the annotations series (last series)
            const seriesIndex = opts.seriesIndex;
            const totalSeries = opts.w.config.series.length;

            // If this is the last series (annotations axis), return empty
            if (seriesIndex === totalSeries - 1) {
                return '';
            }

            return getTooltipHtml(opts, metrics, t, visibleSeries, annotationData, alertTagStyles, normalizeKey, batteryInfo, selectedEntity);
        }
        return tooltipSettings
    }

    const isDataSeriesEmpty = () => {
        return isBaseDataSeriesEmpty(series)
    }

    const getCanvasSettings = () => {
        return getBaseCanvasSettings(primaryGranularity, series)
    }

    // Wrapper for setting up tooltips
    const setupAnnotationTooltips = (chartContext) => {
        // Clean up previous tooltips if any
        if (tooltipCleanupRef.current) {
            tooltipCleanupRef.current();
        }

        const filteredAnnotations = getFilteredAnnotations();

        // Grouping function that returns only visible time points
        const groupVisibleAnnotations = (annotations) => getVisibleTimePoints(annotations);

        // Setup new tooltips and store cleanup function
        tooltipCleanupRef.current = sharedSetupAnnotationTooltips(
            chartContext,
            filteredAnnotations,
            groupVisibleAnnotations,
            getAnnotationTooltipHtml
        );
    };

    const getAnnotations = (yAxisSettings) => {
        const annotations = {
            xaxis: [],
            points: [],
        }

        if (!annotationData || annotationData.length === 0) {
            return annotations;
        }

        const annotationAxisIndex = getAnnotationAxisIndex(yAxisSettings);
        const yMax = getAnnotationYMax(yAxisSettings, annotationAxisIndex);

        // Step 1: Filter annotations based on zoom window (for markers/icons only)
        const filteredAnnotations = getFilteredAnnotations();

        // Step 2: Get visible time points (grouped and filtered) - for markers/icons only
        const visibleTimePoints = getVisibleTimePoints(filteredAnnotations);

        // Step 3: Build severity intervals for fill regions
        // Use grouped time points to ensure fill regions align with annotation markers
        // Also handles annotations that span the entire visible range
        const boundaryTimePoints = getBoundaryTimePoints(zoomedRange, startDate);
        const timeEvents = buildTimeEventsFromGroupedPoints(visibleTimePoints, boundaryTimePoints, annotationData);

        // Build severity intervals if there are any time events
        if (timeEvents.length > 0) {
            const severityIntervals = buildSeverityIntervals(timeEvents);

            severityIntervals.forEach((event) => {
                const { x1, x2, fillColor } = event;
                annotations.xaxis.push({
                    x: x1,
                    x2: x2,
                    fillColor: fillColor,
                    opacity: 0.3,
                });
            });
        }

        // Step 4: Render vertical lines and icons for visible time points only
        visibleTimePoints.forEach((timePoint) => {
            const { timestamp, annotations: groupAnnotations, type, uniqueCount, severityConfig } = timePoint;

            // Add vertical line
            annotations.xaxis.push({
                x: timestamp,
                borderColor: '#BBBBBB',
                strokeDashArray: 0,
            });

            const severityType = type === 'end' ? 'resolved' : severityConfig.severity;

            const icon = getIncidentIconSvg({severity: severityType, count: uniqueCount});

            // Add point marker with icon and optional count badge
            annotations.points.push({
                x: timestamp,
                y: yMax,
                yAxisIndex: annotationAxisIndex,
                offsetY: 10,
                marker: {
                    size: 0,
                },
                image: {
                    path: svgToDataUrl(icon),
                    width: 25,
                    height: 25,
                    offsetX: 0,
                    offsetY: 0
                },
                // Store annotations for tooltip
                customData: {
                    annotations: groupAnnotations,
                    count: uniqueCount,
                    type: type
                }
            });
        });

        return annotations;
    };

    const getChartOptions = () => {
        const yAxisSettings = getYAxisSettings();

        return {
            ...getCanvasSettings(),
            chart: getChartSettings(),
            xaxis: getXAxisSettings(),
            yaxis: yAxisSettings,
            tooltip: getToolTipSettings(),
            annotations: getAnnotations(yAxisSettings),
        };
    }

    const getSeriesForChart = () => {
        return sharedGetSeriesForChart(series, metrics, startDate);
    }

    if (isLoading) {
        return (
            <Box sx={{ height: 475, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CartLoader />
            </Box>
        );
    }

    return (
        <Box sx={{ height: 475, width: '100%' }}>
            <Grid2 container alignItems={'center'} gap={'12px'} sx={{ px: 5, my: 1, maxWidth: 'calc(100vw - 150px)' }}>
                <RenderChartLegend chartIDs={[chartID]} legendSeries={series} isLoading={isLoading} />
            </Grid2>
            <BaseChart
                options={getChartOptions()}
                series={getSeriesForChart()}
                chartID={chartID}
                chartHeight={450}
                primaryGranularity={primaryGranularity}
                resetToLatestDataDate={resetToLatestDataDate}
                isDataSeriesEmpty={isDataSeriesEmpty}
                chartKey={Math.random()}
            />
        </Box>
    );
};

export default ContinuousSignalChart;
